/* eslint-disable max-len */
import React, {
  useEffect, useMemo, useRef, useState,
} from 'react';
import {
  Alert, Badge, Button, Card, Form, Spinner,
} from '@openedx/paragon';
import { useIntl } from '@edx/frontend-platform/i18n';

import {
  cancelSkillBuilderJob,
  createSkillBuilderSourceSession,
  downloadSkillBuilderOlxArchive,
  getSkillBuilderJob,
  getSkillBuilderJobResult,
  retrySkillBuilderJob,
  startSkillBuilderJob,
  uploadSkillBuilderSource,
} from '../data/api';
import useSkillBuilderJob from '../hooks/useSkillBuilderJob';
import messages from '../messages';
import {
  CourseContentPreview,
  KnowledgeMapPreview,
  OutlinePreview,
} from './CoursePreviews';
import JobStatus from './JobStatus';

interface SkillSuggestion {
  id: string;
  name: string;
  source: string;
  relevance_score: number;
  description?: string;
  category?: string;
}

interface SkillSearchResult {
  field_query: string;
  suggested_skills: SkillSuggestion[];
  additional_options: SkillSuggestion[];
}

interface UploadedSource {
  sourceId: string;
  name: string;
  jobId: string;
  status: string;
}

const JOB_ROLE_EXAMPLES = [
  'Leadership',
  'Project management',
  'Sales',
  'Customer service',
  'Data analysis',
  'Software engineering',
  'Marketing',
  'Human resources',
  'Operations',
  'Finance',
  'Healthcare',
  'Supply chain',
  'Retail',
  'Team management',
  'Account management',
  'Business development',
  'Customer success',
  'Training',
  'Compliance',
  'Logistics',
];
const SKILL_AREA_EXAMPLES = [
  'Communication',
  'Negotiation',
  'Conflict resolution',
  'Facilitation',
  'Feedback',
  'Collaboration',
  'Storytelling',
  'Presentation',
  'Coaching',
  'Leadership',
  'Change management',
  'Decision making',
  'Performance management',
  'Problem solving',
  'Adaptability',
  'Innovation',
  'Diversity',
  'Inclusion',
  'Artificial intelligence',
  'Process improvement',
];
const SOURCE_ACCEPT = '.pdf,.docx,.txt,.md';
// Keep this local-only recovery tool out of deployed authoring workflows.
const ENABLE_RESUME_JOB_DEBUG = false;

const isWaiting = (status?: string) => ['queued', 'running', 'cancel_requested'].includes(status || '');

const sourceStatusVariant = (status: string) => {
  if (status === 'completed') { return 'success'; }
  if (status === 'failed') { return 'danger'; }
  return 'warning';
};

const downloadBlob = (blob: Blob, filename: string) => {
  const url = window.URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.URL.revokeObjectURL(url);
};

const FullCourseTab: React.FC = () => {
  const intl = useIntl();
  const wizardSteps = [
    intl.formatMessage(messages.wizardFindSkills),
    intl.formatMessage(messages.wizardSelectSkills),
    intl.formatMessage(messages.wizardAddSources),
    intl.formatMessage(messages.wizardKnowledgeMap),
    intl.formatMessage(messages.wizardOutlineCourse),
  ];
  const [activeStep, setActiveStep] = useState(0);
  const [field, setField] = useState('');
  const [skillSearchJobId, setSkillSearchJobId] = useState<string | null>(null);
  const [isLoadingMoreSkills, setIsLoadingMoreSkills] = useState(false);
  const [hasMoreSkills, setHasMoreSkills] = useState(false);
  const [skillResults, setSkillResults] = useState<SkillSearchResult | null>(null);
  const [selectedSkills, setSelectedSkills] = useState<SkillSuggestion[]>([]);
  const [sourceSessionId, setSourceSessionId] = useState<string | null>(null);
  const [sources, setSources] = useState<UploadedSource[]>([]);
  const [isUploadingSource, setIsUploadingSource] = useState(false);
  const [knowledgeMapJobId, setKnowledgeMapJobId] = useState<string | null>(null);
  const [knowledgeMapRetryVersion, setKnowledgeMapRetryVersion] = useState(0);
  const [outlineJobId, setOutlineJobId] = useState<string | null>(null);
  const [courseJobId, setCourseJobId] = useState<string | null>(null);
  const [knowledgeMapResult, setKnowledgeMapResult] = useState<unknown>(null);
  const [outlineResult, setOutlineResult] = useState<unknown>(null);
  const [courseResult, setCourseResult] = useState<unknown>(null);
  const [error, setError] = useState<string | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [resumeJobId, setResumeJobId] = useState('');
  const [isResumingJob, setIsResumingJob] = useState(false);
  const loadedSkillSearchJobs = useRef(new Set<string>());
  const skillSearch = useSkillBuilderJob(skillSearchJobId);
  const knowledgeMap = useSkillBuilderJob(knowledgeMapJobId, 10000, knowledgeMapRetryVersion);
  const outline = useSkillBuilderJob(outlineJobId);
  const course = useSkillBuilderJob(courseJobId);

  const allSkills = useMemo(() => (
    skillResults ? [...skillResults.suggested_skills, ...skillResults.additional_options] : []
  ), [skillResults]);
  const sourceProcessing = sources.some(source => isWaiting(source.status));
  const readySourceIds = sources.filter(source => source.status === 'completed').map(source => source.sourceId);

  useEffect(() => {
    const skillSearchJob = skillSearch.job;
    if (
      !skillSearchJobId
      || !skillSearchJob
      || skillSearchJob.status !== 'completed'
      || skillSearchJob.job_id !== skillSearchJobId
      || loadedSkillSearchJobs.current.has(skillSearchJobId)
    ) { return; }
    loadedSkillSearchJobs.current.add(skillSearchJobId);
    getSkillBuilderJobResult(skillSearchJobId)
      .then(result => {
        const nextResult = result as SkillSearchResult;
        const nextSkills = [...nextResult.suggested_skills, ...nextResult.additional_options];
        if (isLoadingMoreSkills) {
          const existingIds = new Set(allSkills.map(skill => skill.id));
          const additions = nextSkills.filter(skill => !existingIds.has(skill.id));
          setSkillResults(current => {
            if (!current) { return nextResult; }
            return {
              ...current,
              suggested_skills: [...current.suggested_skills, ...current.additional_options, ...additions],
              additional_options: [],
            };
          });
          setHasMoreSkills(additions.length === nextSkills.length && additions.length === 15);
          setIsLoadingMoreSkills(false);
        } else {
          setSkillResults(nextResult);
          setHasMoreSkills(nextSkills.length === 15);
          setActiveStep(1);
        }
      })
      .catch(() => {
        loadedSkillSearchJobs.current.delete(skillSearchJobId);
        setIsLoadingMoreSkills(false);
        setError(intl.formatMessage(messages.resultLoadError));
      });
  }, [allSkills, intl, isLoadingMoreSkills, skillSearch.job?.status, skillSearchJobId]);

  useEffect(() => {
    const fetchResult = async (jobId: string | null, status: string | undefined, setter: (value: unknown) => void, current: unknown) => {
      if (status === 'awaiting_approval' || status === 'completed') {
        if (jobId && current === null) {
          try { setter(await getSkillBuilderJobResult(jobId)); } catch { setError(intl.formatMessage(messages.resultLoadError)); }
        }
      }
    };
    fetchResult(knowledgeMapJobId, knowledgeMap.job?.status, setKnowledgeMapResult, knowledgeMapResult);
    fetchResult(outlineJobId, outline.job?.status, setOutlineResult, outlineResult);
    fetchResult(courseJobId, course.job?.status, setCourseResult, courseResult);
  }, [course.job?.status, courseJobId, courseResult, intl, knowledgeMap.job?.status, knowledgeMapJobId, knowledgeMapResult, outline.job?.status, outlineJobId, outlineResult]);

  useEffect(() => {
    if (!sources.some(source => isWaiting(source.status))) { return undefined; }
    let cancelled = false;
    const refresh = async () => {
      const statuses = await Promise.all(sources.map(async source => {
        if (!isWaiting(source.status)) { return source; }
        try {
          const job = await getSkillBuilderJob(source.jobId);
          return { ...source, status: job.status };
        } catch { return source; }
      }));
      if (!cancelled) { setSources(statuses); }
    };
    refresh();
    const interval = window.setInterval(refresh, 10000);
    return () => { cancelled = true; window.clearInterval(interval); };
  }, [sources]);

  const resetDownstream = () => {
    setKnowledgeMapJobId(null);
    setOutlineJobId(null);
    setCourseJobId(null);
    setKnowledgeMapResult(null);
    setOutlineResult(null);
    setCourseResult(null);
  };

  const findSkills = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!field.trim()) { return; }
    setError(null);
    setSkillResults(null);
    setIsLoadingMoreSkills(false);
    setHasMoreSkills(false);
    setSelectedSkills([]);
    setSources([]);
    setSourceSessionId(null);
    resetDownstream();
    try {
      const job = await startSkillBuilderJob({ stage: 'skill_identification', input: { field: field.trim() } });
      setSkillSearchJobId(job.job_id);
    } catch { setError(intl.formatMessage(messages.jobSubmitError)); }
  };

  const findMoreSkills = async () => {
    if (!field.trim() || !allSkills.length) { return; }
    setError(null);
    setIsLoadingMoreSkills(true);
    try {
      const job = await startSkillBuilderJob({
        stage: 'skill_identification',
        input: { field: field.trim(), exclude_skill_ids: allSkills.map(skill => skill.id) },
      });
      setSkillSearchJobId(job.job_id);
    } catch {
      setIsLoadingMoreSkills(false);
      setError(intl.formatMessage(messages.jobSubmitError));
    }
  };

  const resumeJob = async (event: React.FormEvent) => {
    event.preventDefault();
    const jobId = resumeJobId.trim();
    if (!jobId) { return; }
    setError(null);
    setIsResumingJob(true);
    try {
      const job = await getSkillBuilderJob(jobId);
      resetDownstream();
      if (job.stage === 'knowledge_map') {
        setKnowledgeMapJobId(job.job_id);
        if (job.status === 'awaiting_approval' || job.status === 'completed') {
          setKnowledgeMapResult(await getSkillBuilderJobResult(job.job_id));
        }
        setActiveStep(3);
      } else if (job.stage === 'outline') {
        setOutlineJobId(job.job_id);
        if (job.status === 'awaiting_approval' || job.status === 'completed') {
          setOutlineResult(await getSkillBuilderJobResult(job.job_id));
        }
        setActiveStep(4);
      } else if (job.stage === 'course') {
        setCourseJobId(job.job_id);
        if (job.status === 'completed') {
          setCourseResult(await getSkillBuilderJobResult(job.job_id));
        }
        setActiveStep(4);
      } else {
        setError(intl.formatMessage(messages.resumeUnsupportedJob));
      }
    } catch {
      setError(intl.formatMessage(messages.resumeJobError));
    } finally {
      setIsResumingJob(false);
    }
  };

  const toggleSkill = (skill: SkillSuggestion) => {
    setSelectedSkills(current => {
      if (current.some(selected => selected.id === skill.id)) {
        resetDownstream();
        return current.filter(selected => selected.id !== skill.id);
      }
      if (current.length >= 3) { return current; }
      resetDownstream();
      return [...current, skill];
    });
  };

  const handleSkillCardKeyDown = (event: React.KeyboardEvent<HTMLElement>, skill: SkillSuggestion) => {
    if (event.target !== event.currentTarget || !['Enter', ' '].includes(event.key)) { return; }
    event.preventDefault();
    toggleSkill(skill);
  };

  const uploadSources = async (files: FileList | null) => {
    if (!files?.length) { return; }
    setError(null);
    setIsUploadingSource(true);
    try {
      let sessionId = sourceSessionId;
      if (!sessionId) {
        const session = await createSkillBuilderSourceSession();
        sessionId = session.job_id;
        setSourceSessionId(sessionId);
      }
      const uploads = await Promise.all(Array.from(files).map(async file => {
        const upload = await uploadSkillBuilderSource(sessionId as string, file);
        return {
          sourceId: upload.source_id, name: file.name, jobId: upload.job_id, status: upload.status,
        };
      }));
      setSources(current => [...current, ...uploads]);
      resetDownstream();
    } catch { setError(intl.formatMessage(messages.sourceUploadError)); } finally { setIsUploadingSource(false); }
  };

  const submitKnowledgeMap = async () => {
    setError(null);
    try {
      const job = await startSkillBuilderJob({
        stage: 'knowledge_map',
        input: { skills: selectedSkills.map(skill => skill.name) },
        source_session_id: sourceSessionId || undefined,
        source_ids: readySourceIds,
      });
      resetDownstream();
      setKnowledgeMapJobId(job.job_id);
    } catch { setError(intl.formatMessage(messages.jobSubmitError)); }
  };

  const approveKnowledgeMap = async () => {
    if (!knowledgeMapJobId) { return; }
    setError(null);
    try {
      const job = await startSkillBuilderJob({ stage: 'outline', input: {}, prerequisite_job_id: knowledgeMapJobId });
      setOutlineJobId(job.job_id);
      setOutlineResult(null);
      setCourseJobId(null);
      setCourseResult(null);
      setActiveStep(4);
    } catch { setError(intl.formatMessage(messages.jobSubmitError)); }
  };

  const approveOutline = async () => {
    if (!outlineJobId) { return; }
    setError(null);
    try {
      const job = await startSkillBuilderJob({ stage: 'course', input: {}, prerequisite_job_id: outlineJobId });
      setCourseJobId(job.job_id);
      setCourseResult(null);
    } catch { setError(intl.formatMessage(messages.jobSubmitError)); }
  };

  const retryKnowledgeMap = async () => {
    if (!knowledgeMapJobId) { return; }
    setError(null);
    try {
      await retrySkillBuilderJob(knowledgeMapJobId);
      setKnowledgeMapResult(null);
      setKnowledgeMapRetryVersion(version => version + 1);
    } catch { setError(intl.formatMessage(messages.jobSubmitError)); }
  };

  const downloadOlx = async () => {
    if (!courseJobId) { return; }
    setIsDownloading(true);
    setError(null);
    try {
      const { blob, filename } = await downloadSkillBuilderOlxArchive(courseJobId);
      downloadBlob(blob, filename);
    } catch { setError(intl.formatMessage(messages.downloadError)); } finally { setIsDownloading(false); }
  };

  const canNavigateTo = (step: number) => {
    if (step === 0) { return true; }
    if (step === 1) { return Boolean(skillResults); }
    if (step === 2 || step === 3) { return selectedSkills.length > 0; }
    return Boolean(knowledgeMapResult);
  };

  return (
    <section className="ai-assisted-course-authoring-tab__workspace" aria-labelledby="full-course-heading">
      <h3 id="full-course-heading">{intl.formatMessage(messages.fullCourseHeading)}</h3>
      <p>{intl.formatMessage(messages.fullCourseDescription)}</p>
      {error && <Alert variant="danger">{error}</Alert>}

      {ENABLE_RESUME_JOB_DEBUG && (
        <Card className="ai-assisted-course-authoring-tab__resume-job-card">
          <Card.Body className="ai-assisted-course-authoring-tab__card-body">
            <h4>{intl.formatMessage(messages.resumeJobHeading)}</h4>
            <p>{intl.formatMessage(messages.resumeJobDescription)}</p>
            <form onSubmit={resumeJob} className="ai-assisted-course-authoring-tab__resume-job-form">
              <Form.Label htmlFor="resume-skill-builder-job">{intl.formatMessage(messages.resumeJobLabel)}</Form.Label>
              <Form.Control id="resume-skill-builder-job" value={resumeJobId} onChange={(event: React.ChangeEvent<HTMLInputElement>) => setResumeJobId(event.target.value)} placeholder={intl.formatMessage(messages.resumeJobPlaceholder)} />
              <Button type="submit" disabled={!resumeJobId.trim() || isResumingJob}>{isResumingJob ? intl.formatMessage(messages.resumingJob) : intl.formatMessage(messages.resumeJobButton)}</Button>
            </form>
          </Card.Body>
        </Card>
      )}

      <nav aria-label={intl.formatMessage(messages.wizardNavigationLabel)}>
        <ol className="ai-assisted-course-authoring-tab__wizard-steps">
          {wizardSteps.map((label, index) => (
            <li key={label} className={index === activeStep ? 'is-active' : ''}>
              <Button variant="tertiary" size="sm" disabled={!canNavigateTo(index)} aria-current={index === activeStep ? 'step' : undefined} onClick={() => setActiveStep(index)}>
                <span aria-hidden="true">{index + 1}. </span>{label}
              </Button>
            </li>
          ))}
        </ol>
      </nav>

      {activeStep === 0 && (
        <form className="ai-assisted-course-authoring-tab__skill-search" onSubmit={findSkills}>
          <Form.Group>
            <Form.Label>{intl.formatMessage(messages.jobRoleLabel)}</Form.Label>
            <Form.Text>{intl.formatMessage(messages.jobRoleHelp)}</Form.Text>
            <div className="ai-assisted-course-authoring-tab__example-groups">
              <div><strong>{intl.formatMessage(messages.jobRolesHeading)}</strong><div>{JOB_ROLE_EXAMPLES.map(example => <Button key={example} type="button" size="sm" variant={field.trim().toLowerCase() === example.toLowerCase() ? 'primary' : 'outline-secondary'} className="ai-assisted-course-authoring-tab__example-pill" aria-pressed={field.trim().toLowerCase() === example.toLowerCase()} onClick={() => setField(example)}>{example}</Button>)}</div></div>
              <div><strong>{intl.formatMessage(messages.skillAreasHeading)}</strong><div>{SKILL_AREA_EXAMPLES.map(example => <Button key={example} type="button" size="sm" variant={field.trim().toLowerCase() === example.toLowerCase() ? 'primary' : 'outline-secondary'} className="ai-assisted-course-authoring-tab__example-pill" aria-pressed={field.trim().toLowerCase() === example.toLowerCase()} onClick={() => setField(example)}>{example}</Button>)}</div></div>
            </div>
            <div className="ai-assisted-course-authoring-tab__search-control">
              <Form.Control value={field} onChange={(event: React.ChangeEvent<HTMLInputElement>) => setField(event.target.value)} placeholder={intl.formatMessage(messages.jobRolePlaceholder)} />
              <Button type="submit" disabled={!field.trim() || isWaiting(skillSearch.job?.status)}>{intl.formatMessage(messages.findSkillsButton)}</Button>
            </div>
          </Form.Group>
          {skillSearchJobId && <JobStatus job={skillSearch.job} error={skillSearch.error} onCancel={() => cancelSkillBuilderJob(skillSearchJobId)} />}
        </form>
      )}

      {activeStep === 1 && skillResults && (
        <div className="ai-assisted-course-authoring-tab__skill-results">
          <div className="ai-assisted-course-authoring-tab__skill-results-heading"><h4>{intl.formatMessage(messages.selectSkillsHeading)}</h4><Badge variant="light">{intl.formatMessage(messages.selectedSkillsCount, { count: selectedSkills.length })}</Badge></div>
          <p>{intl.formatMessage(messages.skillResultsDescription, { field: skillResults.field_query })}</p>
          <div className="ai-assisted-course-authoring-tab__skill-results-scroll">
            <div className="ai-assisted-course-authoring-tab__skill-grid">
              {allSkills.map(skill => {
                const selected = selectedSkills.some(item => item.id === skill.id);
                return <Card key={skill.id} isClickable role="button" aria-pressed={selected} onClick={() => toggleSkill(skill)} onKeyDown={(event: React.KeyboardEvent<HTMLElement>) => handleSkillCardKeyDown(event, skill)} className={`ai-assisted-course-authoring-tab__skill-card${selected ? ' ai-assisted-course-authoring-tab__skill-card--selected' : ''}`}><Card.Body><Form.Checkbox checked={selected} disabled={!selected && selectedSkills.length >= 3} onClick={(event: React.MouseEvent) => event.stopPropagation()} onChange={() => toggleSkill(skill)}>{skill.name}</Form.Checkbox>{skill.category && <Badge variant="light">{skill.category}</Badge>}<p>{intl.formatMessage(messages.marketDemandLabel, { score: Math.round(skill.relevance_score * 100) })}</p>{skill.description && <p>{skill.description}</p>}</Card.Body></Card>;
              })}
            </div>
          </div>
          <div className="ai-assisted-course-authoring-tab__skill-results-actions">
            {hasMoreSkills && <Button className="ai-assisted-course-authoring-tab__primary-action" disabled={isLoadingMoreSkills || isWaiting(skillSearch.job?.status)} onClick={findMoreSkills}>{isLoadingMoreSkills ? <><Spinner className="ai-assisted-course-authoring-tab__find-more-spinner" animation="border" size="sm" />Finding more...</> : 'Find more'}</Button>}
            <Button className="ai-assisted-course-authoring-tab__primary-action" disabled={!selectedSkills.length} onClick={() => setActiveStep(2)}>{intl.formatMessage(messages.continueButton)}</Button>
          </div>
        </div>
      )}

      {activeStep === 2 && (
        <Card className="ai-assisted-course-authoring-tab__source-card">
          <Card.Body className="ai-assisted-course-authoring-tab__card-body">
            <h4>{intl.formatMessage(messages.ragSourcesHeading)}</h4>
            <p>{intl.formatMessage(messages.ragSourcesDescription)}</p>
            <Form.Group>
              <Form.Label>{intl.formatMessage(messages.ragSourcesInputLabel)}</Form.Label>
              <Form.Control type="file" accept={SOURCE_ACCEPT} multiple onChange={(event: React.ChangeEvent<HTMLInputElement>) => uploadSources(event.target.files)} disabled={isUploadingSource} />
            </Form.Group>
            {isUploadingSource && <p><Spinner animation="border" size="sm" /> {intl.formatMessage(messages.uploadingSources)}</p>}
            {sources.length > 0 && <ul className="ai-assisted-course-authoring-tab__source-list">{sources.map(source => <li key={source.sourceId}><span>{source.name}</span><Badge variant={sourceStatusVariant(source.status)}>{source.status}</Badge></li>)}</ul>}
            {sourceProcessing && <Alert variant="warning">{intl.formatMessage(messages.sourcesProcessing)}</Alert>}
            <Button onClick={() => setActiveStep(3)} disabled={sourceProcessing}>{intl.formatMessage(messages.continueButton)}</Button>
          </Card.Body>
        </Card>
      )}

      {activeStep === 3 && (
        <div>
          <Card className="ai-assisted-course-authoring-tab__review-card"><Card.Body className="ai-assisted-course-authoring-tab__card-body"><h4>{intl.formatMessage(messages.knowledgeMapHeading)}</h4><p>{intl.formatMessage(messages.knowledgeMapDescription)}</p><p>{selectedSkills.map(skill => <Badge key={skill.id} variant="light">{skill.name}</Badge>)}</p><Button disabled={isWaiting(knowledgeMap.job?.status) || sourceProcessing} onClick={submitKnowledgeMap}>{intl.formatMessage(messages.generateKnowledgeMapButton)}</Button></Card.Body></Card>
          {knowledgeMapJobId && <JobStatus job={knowledgeMap.job} error={knowledgeMap.error} onCancel={() => cancelSkillBuilderJob(knowledgeMapJobId)} onRetry={retryKnowledgeMap} />}
          {knowledgeMapResult && <><KnowledgeMapPreview result={knowledgeMapResult} /><Button onClick={approveKnowledgeMap}>{intl.formatMessage(messages.approveKnowledgeMapButton)}</Button></>}
        </div>
      )}

      {activeStep === 4 && (
        <div>
          {!outlineJobId && <Alert variant="info">{intl.formatMessage(messages.approveKnowledgeMapFirst)}</Alert>}
          {outlineJobId && <JobStatus job={outline.job} error={outline.error} onCancel={() => cancelSkillBuilderJob(outlineJobId)} />}
          {outlineResult && <><OutlinePreview result={outlineResult} /><Button disabled={isWaiting(course.job?.status)} onClick={approveOutline}>{intl.formatMessage(messages.approveOutlineButton)}</Button></>}
          {courseJobId && <JobStatus job={course.job} error={course.error} onCancel={() => cancelSkillBuilderJob(courseJobId)} />}
          {courseResult && <><CourseContentPreview result={courseResult} outlineResult={outlineResult} /><Button className="ai-assisted-course-authoring-tab__primary-action" disabled={isDownloading} onClick={downloadOlx}>{isDownloading ? intl.formatMessage(messages.downloadingArchive) : intl.formatMessage(messages.downloadOlxButton)}</Button></>}
        </div>
      )}
    </section>
  );
};

export default FullCourseTab;
